import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Cartao } from '../../componentes/base';
import { Carregando, Erro } from '../../componentes/estados';
import { Selecao } from '../../componentes/formulario';
import { get } from '../../nucleo/api';
import { data, dinheiro, dinheiroPorMoeda, somaPorMoeda, nomePessoa, numero, textoLivre } from '../../nucleo/formato';
import { rotuloEtapaPipeline, rotuloMotivoPerda } from '../../nucleo/rotulos';
import { caminhoDaNegociacao } from './crm-caminhos';

/**
 * Lote crm8 (item 6, 29/10): os tipos de relatório da origem além do Dashboard (Relatórios > "Tipo de relatório"): Painel
 * (o de entrada), Pipeline, Equipes, Feedback, Conversões, Produtos e Serviços, Fontes e Campanhas, Metas, Ligações e
 * Comparativo de ligações. Os blocos, as abas e os gráficos são os da origem (linha dos últimos 30 dias, rosca por etapa,
 * barras horizontais e o funil), com os tokens do MalgoOS. Os dados vêm de `/pipeline/crm/relatorio/negociacoes` (o recorte do
 * funil com os filtros, resumido por negociação) e, nas Metas, de `/pipeline/metas`. Valores sempre na moeda de cada
 * negociação pelo formatador (com o modo discreto). Lista de 04/11, item 3: somas nunca misturam moedas: uma por moeda, a do
 * workspace primeiro, e cada gráfico de valor sai uma vez por moeda (com uma moeda só, igual a antes).
 */

export const TIPOS_RELATORIO = ['painel', 'dashboard', 'pipeline', 'equipes', 'feedback', 'conversoes', 'produtos', 'fontes', 'metas', 'ligacoes', 'comparativo'] as const;
export type TipoRelatorio = (typeof TIPOS_RELATORIO)[number];
/** Como na origem: os de equipes, metas e ligações não têm funil; Equipes e Metas não têm responsável. */
export const semFunil = (t: TipoRelatorio) => t === 'equipes' || t === 'metas' || t === 'ligacoes' || t === 'comparativo';
export const semResponsavel = (t: TipoRelatorio) => t === 'equipes' || t === 'metas';

export interface NegociacaoRel {
  id: string;
  titulo: string;
  parceiro_nome?: string | null;
  etapa_id: string;
  responsavel_id: string | null;
  responsavel_nome: string | null;
  equipe_id: string | null;
  equipe_nome: string | null;
  fonte: string | null;
  campanha: string | null;
  moeda: string;
  total_centavos: number;
  unico_centavos: number;
  recorrente_centavos: number;
  estado: 'aberta' | 'ganha' | 'perdida';
  pausada: boolean;
  created_at: string;
  ganha_em: string | null;
  perdida_em: string | null;
  motivo_perda: string | null;
  ultimo_contato: string | null;
  tarefas_pendentes: number;
  produtos: { nome: string; quantidade: number; total_centavos: number }[];
}
export interface DadosRelatorioTipos {
  funil_id: string;
  etapas: { id: string; nome: string; tipo: string }[];
  negociacoes: NegociacaoRel[];
  truncado: boolean;
  ligacoes_truncado?: boolean;
  ligacoes: {
    responsavel_id: string | null;
    responsavel_nome: string | null;
    equipe_nome: string | null;
    concluida_em: string;
  }[];
}

const DIA = 86_400_000;
/** As cores das etapas na rosca e no funil (a paleta da origem nos tokens do MalgoOS). */
const CORES = ['var(--color-verde)', 'var(--color-ciano)', 'var(--color-dourado)', 'var(--color-roxo)', 'var(--color-laranja)', 'var(--color-azul)', 'var(--color-perigo)'];
const cor = (i: number) => CORES[i % CORES.length]!;

/** Os 30 dias até hoje (meia-noite local), para as linhas do Painel e das Ligações. */
export function ultimos30Dias(agora = new Date()): Date[] {
  const hoje = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());
  // Rodada rlote-5 (ROB-B2): aritmética de calendário (o dia da troca do horário de verão não some nem a janela vira 31 dias).
  return Array.from({ length: 30 }, (_, i) => new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - (29 - i)));
}
/** Quantos (ou quanto, com `peso`) por dia, pela data `quando` de cada item. */
export function porDia<T>(itens: T[], quando: (x: T) => string | null, dias: Date[], peso: (x: T) => number = () => 1): number[] {
  const chave = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  const mapa = new Map(dias.map((d) => [chave(d), 0]));
  for (const x of itens) {
    const q = quando(x);
    if (!q) continue;
    const k = chave(new Date(q));
    if (mapa.has(k)) mapa.set(k, mapa.get(k)! + peso(x));
  }
  return dias.map((d) => mapa.get(chave(d))!);
}
/** Faixa do último contato do Feedback (como na origem: até 7 dias, 8 a 30, mais de 30, sem contato). */
export function faixaDoContato(ultimo: string | null, agora = Date.now()): 'ate7' | 'ate30' | 'mais30' | 'sem' {
  if (!ultimo) return 'sem';
  const dias = (agora - new Date(ultimo).getTime()) / DIA;
  return dias < 7 ? 'ate7' : dias < 30 ? 'ate30' : 'mais30';
}
/** Conversões: quantas negociações chegaram a cada etapa (a etapa atual na ordem do funil, ou adiante). */
export function chegaramEm(etapas: { id: string }[], ns: { etapa_id: string }[]): number[] {
  const idx = new Map(etapas.map((e, i) => [e.id, i]));
  return etapas.map((_, i) => ns.filter((n) => (idx.get(n.etapa_id) ?? -1) >= i).length);
}
/** Soma por chave (ordem da primeira aparição); `valor` padrão conta 1. */
export function agrupar<T>(itens: T[], chave: (x: T) => string, valor: (x: T) => number = () => 1): [string, number][] {
  const m = new Map<string, number>();
  for (const x of itens) m.set(chave(x), (m.get(chave(x)) ?? 0) + valor(x));
  return [...m];
}

export function RelatorioTipos({ tipo, params, moeda }: { tipo: Exclude<TipoRelatorio, 'dashboard'>; params: Record<string, string>; moeda: string }) {
  const { t } = useTranslation('pipeline');
  const precisaNegociacoes = tipo !== 'metas';
  const q = useQuery({
    queryKey: ['pipeline', 'crm', 'relatorio', 'tipos', params],
    queryFn: () => get<DadosRelatorioTipos>('/pipeline/crm/relatorio/negociacoes', params),
    enabled: precisaNegociacoes,
    placeholderData: keepPreviousData,
  });
  if (tipo === 'metas') return <Metas moeda={moeda} />;
  if (q.isError && !q.data) return <Erro erro={q.error} aoTentar={() => void q.refetch()} />;
  if (!q.data) return <Carregando texto={t('crm.relatorio.carregando')} />;
  // Rlote-10 (VIS-M1): as etapas de fábrica (gravadas em pt) saem no idioma do app em todos os tipos (rosca, tabela, barras,
  // funil, Feedback e conversão), como no quadro; etapa criada pelo cliente fica como está.
  const d = { ...q.data, etapas: q.data.etapas.map((e) => ({ ...e, nome: rotuloEtapaPipeline(e.nome) })) };
  return (
    <div className="flex min-w-0 flex-col gap-4" aria-busy={q.isFetching} data-relatorio-tipo={tipo}>
      {/* Rodada rlote-6 (ROB-B2): nas Ligações e no Comparativo as ligações não dependem do teto das negociações. */}
      {d.truncado && tipo !== 'ligacoes' && tipo !== 'comparativo' && (
        <p className="rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm text-tinta-2" role="status" data-relatorio-truncado="">
          {t('crm.relatorio.tipos.truncado', {
            n: numero(d.negociacoes.length),
          })}
        </p>
      )}
      {d.ligacoes_truncado && (tipo === 'ligacoes' || tipo === 'comparativo') && (
        <p className="rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm text-tinta-2" role="status" data-relatorio-ligacoes-truncado="">
          {t('crm.relatorio.tipos.ligacoesTruncado', { n: numero(d.ligacoes.length) })}
        </p>
      )}
      {tipo === 'painel' && <Painel d={d} moeda={moeda} />}
      {tipo === 'pipeline' && <Pipeline d={d} moeda={moeda} />}
      {tipo === 'equipes' && <Equipes d={d} moeda={moeda} />}
      {tipo === 'feedback' && <Feedback d={d} />}
      {tipo === 'conversoes' && <Conversoes d={d} />}
      {tipo === 'produtos' && <Produtos d={d} moeda={moeda} />}
      {tipo === 'fontes' && <Fontes d={d} />}
      {tipo === 'ligacoes' && <Ligacoes d={d} />}
      {tipo === 'comparativo' && <Comparativo d={d} />}
    </div>
  );
}

// ------------------------------------------------------------------------------------------------------------ peças

function Vazio({ texto }: { texto?: string }) {
  const { t } = useTranslation('pipeline');
  return (
    <div className="flex flex-col items-center gap-1 px-4 py-8 text-center text-sm text-tinta-3" data-relatorio-vazio="">
      <p className="text-base font-semibold text-tinta-2">{t('crm.relatorio.tipos.ops')}</p>
      <p>{texto ?? t('crm.relatorio.tipos.opsTexto')}</p>
    </div>
  );
}

function Numeros({ itens }: { itens: { rotulo: string; valor: string; cor?: string; dado: string }[] }) {
  return (
    <dl className={`grid grid-cols-1 gap-3 ${itens.length >= 3 ? 'sm:grid-cols-3' : itens.length === 2 ? 'sm:grid-cols-2' : ''}`}>
      {itens.map((x) => (
        <div key={x.dado} className="flex flex-col items-center gap-1 rounded-lg bg-sup-2 px-3 py-3 text-center" data-numero={x.dado}>
          <dt className={`text-sm ${x.cor ?? 'text-tinta-2'}`}>{x.rotulo}</dt>
          <dd className="numero text-2xl font-bold text-tinta-1">{x.valor}</dd>
        </div>
      ))}
    </dl>
  );
}

function Legenda({ itens }: { itens: [string, string][] }) {
  return (
    <ul className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-tinta-2">
      {itens.map(([rotulo, c]) => (
        <li key={rotulo} className="flex items-center gap-1.5">
          <span
            className="inline-block h-3 w-6 rounded-sm border-2"
            style={{
              borderColor: c,
              background: `color-mix(in srgb, ${c} 15%, transparent)`,
            }}
            aria-hidden
          />
          {rotulo}
        </li>
      ))}
    </ul>
  );
}

/** Linha dos últimos 30 dias (SVG), uma série por cor, com a grade e as datas a cada 5 dias. */
function GraficoLinha({ series, dias, rotulo, formatar = (v) => numero(v) }: { series: { valores: number[]; cor: string }[]; dias: Date[]; rotulo: string; formatar?: (v: number) => string }) {
  const L = 1100;
  const A = 300;
  const max = Math.max(1, ...series.flatMap((s) => s.valores));
  const x = (i: number) => 60 + (i * (L - 80)) / Math.max(1, dias.length - 1);
  const y = (v: number) => A - 25 - (v / max) * (A - 45);
  return (
    <svg viewBox={`0 0 ${L} ${A}`} className="h-56 w-full sm:h-72" role="img" aria-label={rotulo} data-grafico-linha="">
      {[0, 0.25, 0.5, 0.75, 1].map((k) => (
        <g key={k}>
          <line x1={60} x2={L - 20} y1={y(max * k)} y2={y(max * k)} stroke="var(--color-borda)" />
          <text x={54} y={y(max * k) + 4} fontSize={11} fill="var(--color-tinta-3)" textAnchor="end">
            {formatar(Math.round(max * k * 100) / 100)}
          </text>
        </g>
      ))}
      {series.map((s, n) => (
        <polyline
          key={n}
          fill={`color-mix(in srgb, ${s.cor} 14%, transparent)`}
          stroke={s.cor}
          strokeWidth={2}
          points={`${x(0)},${A - 25} ${s.valores.map((v, i) => `${x(i)},${y(v)}`).join(' ')} ${x(dias.length - 1)},${A - 25}`}
        />
      ))}
      {dias.map((d, i) =>
        i % 5 ? null : (
          <text key={i} x={x(i)} y={A - 6} fontSize={10} fill="var(--color-tinta-3)" textAnchor="middle">
            {data(d.toISOString()).slice(0, 5)}
          </text>
        ),
      )}
    </svg>
  );
}

/** Rosca por etapa (a fatia de cada etapa na cor dela, em SVG), com a legenda das etapas embaixo. */
function Rosca({ etapas, ns }: { etapas: DadosRelatorioTipos['etapas']; ns: NegociacaoRel[] }) {
  const { t } = useTranslation('pipeline');
  const total = ns.length;
  const R = 40;
  const C = 2 * Math.PI * R;
  let acc = 0;
  const fatias = etapas.map((e, i) => {
    const n = ns.filter((x) => x.etapa_id === e.id).length;
    const f = { cor: cor(i), tamanho: (n / Math.max(1, total)) * C, inicio: acc, nome: e.nome, n };
    acc += f.tamanho;
    return f;
  });
  return (
    <div className="flex flex-col gap-3">
      {total ? (
        <svg viewBox="0 0 100 100" className="mx-auto size-48 -rotate-90" role="img" aria-label={fatias.map((f) => `${f.nome}: ${f.n}`).join(', ')} data-rosca="">
          {fatias
            .filter((f) => f.tamanho > 0)
            .map((f) => (
              <circle key={f.nome} cx={50} cy={50} r={R} fill="none" stroke={f.cor} strokeWidth={18} strokeDasharray={`${f.tamanho} ${C - f.tamanho}`} strokeDashoffset={-f.inicio} />
            ))}
        </svg>
      ) : (
        <p className="grid h-48 place-items-center text-sm text-tinta-3">{t('crm.relatorio.tipos.semDados')}</p>
      )}
      <ul className="flex flex-wrap gap-x-3 gap-y-1.5 rounded-lg border border-borda bg-sup-2 p-3 text-xs">
        {etapas.map((e, i) => (
          <li key={e.id} className="flex items-center gap-1.5">
            <span className="inline-block h-3.5 w-5 rounded-sm" style={{ background: cor(i) }} aria-hidden />
            {e.nome}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Barras horizontais: rótulo, barra proporcional ao maior e o valor (o `hbars` da origem). */
function Barras({ linhas, corBarra = 'var(--color-ciano)', formatar = (v) => numero(v), dado }: { linhas: [string, number][]; corBarra?: string; formatar?: (v: number) => string; dado: string }) {
  if (!linhas.length) return <Vazio />;
  const max = Math.max(1, ...linhas.map((l) => l[1]));
  return (
    <ul className="flex flex-col gap-2" data-barras={dado}>
      {linhas.map(([rotulo, v]) => (
        <li key={rotulo} className="flex flex-col gap-1 text-sm" data-barra={rotulo}>
          <span className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 truncate" title={textoLivre(rotulo)}>
              {textoLivre(rotulo)}
            </span>
            <span className="numero shrink-0">{formatar(v)}</span>
          </span>
          <span className="h-3 overflow-hidden rounded-full bg-sup-3">
            <span className="block h-full rounded-full" style={{ width: `${(v / max) * 100}%`, background: corBarra }} />
          </span>
        </li>
      ))}
    </ul>
  );
}

function Abas<T extends string>({ abas, atual, aoMudar, rotulo }: { abas: readonly T[]; atual: T; aoMudar: (a: T) => void; rotulo: (a: T) => string }) {
  return (
    <div role="tablist" className="flex gap-1 border-b border-borda" data-relatorio-abas="">
      {abas.map((a) => (
        <button
          key={a}
          role="tab"
          type="button"
          aria-selected={a === atual}
          onClick={() => aoMudar(a)}
          className="-mb-px border-b-2 border-transparent px-3 py-2 text-sm font-semibold tracking-wide text-tinta-3 uppercase aria-selected:border-acento-forte aria-selected:text-acento-forte"
          data-aba-relatorio={a}
        >
          {rotulo(a)}
        </button>
      ))}
    </div>
  );
}

function TabelaNegociacoes({ ns, etapas }: { ns: NegociacaoRel[]; etapas: DadosRelatorioTipos['etapas'] }) {
  const { t } = useTranslation('pipeline');
  if (!ns.length) return <Vazio />;
  const nomeEtapa = new Map(etapas.map((e) => [e.id, e.nome]));
  return (
    <div className="min-w-0 overflow-x-auto" role="region" aria-label={t('crm.relatorio.tipos.negociacoes')} tabIndex={0}>
      <table className="w-full text-sm" data-tabela-negociacoes="">
        <thead>
          <tr className="border-b border-borda text-left">
            {(['negociacao', 'etapa', 'responsavel', 'fonte', 'valor', 'ultimoContato'] as const).map((c) => (
              <th key={c} scope="col" className="px-3 py-2 text-xs font-semibold tracking-wider whitespace-nowrap text-tinta-3 uppercase">
                {t(`crm.relatorio.tipos.colunas.${c}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ns.map((n) => (
            <tr key={n.id} className="border-t border-borda">
              <td className="px-3 py-2">
                <Link className="font-medium text-acento-forte hover:underline" to={caminhoDaNegociacao(n.id)}>
                  {/* Rodada rlote-7 (VIS-M1): o título com o nome do cliente pelo modo discreto, como no resto do CRM. */}
                  {textoLivre(n.titulo, n.parceiro_nome)}
                </Link>
              </td>
              <td className="px-3 py-2">{nomeEtapa.get(n.etapa_id) ?? ''}</td>
              <td className="px-3 py-2">{n.responsavel_nome ? nomePessoa(n.responsavel_nome) : ''}</td>
              <td className="px-3 py-2">{n.fonte ? textoLivre(n.fonte) : ''}</td>
              <td className="numero px-3 py-2 whitespace-nowrap">{dinheiro(n.total_centavos, n.moeda)}</td>
              <td className="numero px-3 py-2 whitespace-nowrap">{n.ultimo_contato ? data(n.ultimo_contato) : '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const somaEm = (ns: NegociacaoRel[], moeda: string) => dinheiroPorMoeda(somaPorMoeda(ns, (n) => n.moeda, (n) => n.total_centavos, moeda), moeda);
/** As moedas de uma lista, a do workspace primeiro; sem nenhuma, a do workspace (o vazio de sempre). */
const moedasDe = (ns: { moeda: string }[], moeda: string) => {
  const outras = [...new Set(ns.map((n) => n.moeda))].filter((m) => m !== moeda).sort();
  return ns.some((n) => n.moeda === moeda) || !outras.length ? [moeda, ...outras] : outras;
};
/** O bloco de valor uma vez por moeda: com mais de uma, cada um leva o código. */
function PorMoeda({ moedas, children }: { moedas: string[]; children: (moeda: string) => ReactNode }) {
  if (moedas.length < 2) return <>{children(moedas[0]!)}</>;
  return (
    <div className="flex min-w-0 flex-col gap-3">
      {moedas.map((m) => (
        <div key={m} className="flex min-w-0 flex-col gap-1" data-moeda-bloco={m}>
          <p className="numero text-xs font-semibold text-tinta-3">{m}</p>
          {children(m)}
        </div>
      ))}
    </div>
  );
}
const recortes = (d: DadosRelatorioTipos) => {
  const todas = d.negociacoes;
  return {
    todas,
    ganhas: todas.filter((n) => n.estado === 'ganha'),
    perdidas: todas.filter((n) => n.estado === 'perdida'),
    abertas: todas.filter((n) => n.estado === 'aberta'),
  };
};

// ------------------------------------------------------------------------------------------------------------ tipos

function Painel({ d, moeda }: { d: DadosRelatorioTipos; moeda: string }) {
  const { t } = useTranslation('pipeline');
  const { todas, ganhas, perdidas, abertas } = recortes(d);
  const dias = ultimos30Dias();
  const moedasGanhas = moedasDe(ganhas, moeda);
  const abertasEtapas = d.etapas.filter((e) => e.tipo === 'aberta');
  const maxFunil = Math.max(1, ...abertasEtapas.map((e) => abertas.filter((n) => n.etapa_id === e.id).length));
  const porUsuario = (m: string) =>
    agrupar(
      ganhas.filter((n) => n.moeda === m),
      (n) => (n.responsavel_nome ? nomePessoa(n.responsavel_nome) : t('crm.relatorio.tipos.semResponsavel')),
      (n) => n.total_centavos,
    );
  const motivos = agrupar(
    perdidas.flatMap((n) => (n.motivo_perda ? [textoLivre(rotuloMotivoPerda(n.motivo_perda))] : [])),
    (m) => m,
  );
  const semTarefa = abertas.filter((n) => !n.pausada && !n.tarefas_pendentes).length;
  const semInteracao = abertas.filter((n) => !n.pausada && !n.ultimo_contato).length;
  const recentes = abertas.filter((n) => !n.pausada && Date.now() - new Date(n.created_at).getTime() < 7 * DIA).length;
  return (
    <>
      <Cartao titulo={t('crm.relatorio.tipos.painelControle')}>
        <div className="flex flex-col gap-3" data-bloco="painel">
          <Numeros
            itens={[
              {
                rotulo: t('crm.relatorio.resumo.criadas'),
                valor: numero(todas.length),
                cor: 'text-ciano',
                dado: 'criadas',
              },
              {
                rotulo: t('crm.relatorio.resumo.vendas'),
                valor: numero(ganhas.length),
                cor: 'text-verde',
                dado: 'vendas',
              },
              {
                rotulo: t('crm.relatorio.resumo.perdidas'),
                valor: numero(perdidas.length),
                cor: 'text-perigo',
                dado: 'perdidas',
              },
            ]}
          />
          <Legenda
            itens={[
              [t('crm.relatorio.tipos.criadasSerie'), 'var(--color-ciano)'],
              [t('crm.relatorio.tipos.vendasSerie'), 'var(--color-verde)'],
              [t('crm.relatorio.tipos.perdidasSerie'), 'var(--color-perigo)'],
            ]}
          />
          <GraficoLinha
            dias={dias}
            rotulo={t('crm.relatorio.tipos.painelControle')}
            series={[
              {
                valores: porDia(todas, (n) => n.created_at, dias),
                cor: 'var(--color-ciano)',
              },
              {
                valores: porDia(ganhas, (n) => n.ganha_em, dias),
                cor: 'var(--color-verde)',
              },
              {
                valores: porDia(perdidas, (n) => n.perdida_em, dias),
                cor: 'var(--color-perigo)',
              },
            ]}
          />
        </div>
      </Cartao>
      <Cartao titulo={t('crm.relatorio.blocos.valorVolume')}>
        <div className="flex flex-col gap-3" data-bloco="valorVolumePainel">
          <Numeros
            itens={[
              {
                rotulo: t('crm.relatorio.valores.total'),
                valor: somaEm(ganhas, moeda),
                dado: 'valor',
              },
              {
                rotulo: t('crm.relatorio.unidades'),
                valor: numero(ganhas.length),
                dado: 'unidades',
              },
              {
                rotulo: t('crm.relatorio.ticket'),
                valor: dinheiroPorMoeda(
                  moedasGanhas.map((m) => {
                    const daMoeda = ganhas.filter((n) => n.moeda === m);
                    return { moeda: m, valor: daMoeda.length ? Math.round(daMoeda.reduce((a, n) => a + n.total_centavos, 0) / daMoeda.length) : 0 };
                  }),
                  moeda,
                ),
                dado: 'ticket',
              },
            ]}
          />
          <Legenda itens={[[t('crm.relatorio.tipos.valorVendido'), 'var(--color-verde)']]} />
          <PorMoeda moedas={moedasGanhas}>
            {(m) => (
              <GraficoLinha
                dias={dias}
                rotulo={t('crm.relatorio.tipos.valorVendido')}
                formatar={(v) => dinheiro(Math.round(v), m)}
                series={[
                  {
                    valores: porDia(
                      ganhas.filter((n) => n.moeda === m),
                      (n) => n.ganha_em,
                      dias,
                      (n) => n.total_centavos,
                    ),
                    cor: 'var(--color-verde)',
                  },
                ]}
              />
            )}
          </PorMoeda>
        </div>
      </Cartao>
      <h2 className="text-center text-lg font-semibold text-tinta-2">{t('crm.relatorio.tipos.desempenho')}</h2>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Cartao titulo={t('crm.relatorio.tipos.criadas')}>
          <Rosca etapas={d.etapas} ns={todas} />
        </Cartao>
        <Cartao titulo={t('crm.relatorio.tipos.vendas')}>
          <Rosca etapas={d.etapas} ns={ganhas} />
        </Cartao>
        <Cartao titulo={t('crm.relatorio.tipos.perdidas')}>
          <Rosca etapas={d.etapas} ns={perdidas} />
        </Cartao>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Cartao titulo={t('crm.relatorio.tipos.funilCriacao')}>
          <Barras dado="funilCriacao" linhas={d.etapas.map((e) => [`${e.nome}`, todas.filter((n) => n.etapa_id === e.id).length] as [string, number])} />
        </Cartao>
        <Cartao titulo={t('crm.relatorio.tipos.valorUsuarios')}>
          {ganhas.some((n) => n.total_centavos) ? (
            <PorMoeda moedas={moedasGanhas}>{(m) => <Barras dado="valorUsuarios" linhas={porUsuario(m)} corBarra="var(--color-verde)" formatar={(v) => dinheiro(v, m)} />}</PorMoeda>
          ) : (
            <p className="grid h-40 place-items-center text-sm text-tinta-3">{t('crm.relatorio.tipos.semDados')}</p>
          )}
        </Cartao>
        <Cartao titulo={t('crm.relatorio.blocos.perdidas')}>
          {motivos.length ? (
            <Barras dado="motivos" linhas={motivos} corBarra="var(--color-perigo)" />
          ) : (
            <p className="grid h-40 place-items-center text-sm text-tinta-3">{t('crm.relatorio.tipos.semDados')}</p>
          )}
        </Cartao>
      </div>
      <Cartao titulo={t('crm.relatorio.tipos.visaoGeral')}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-bloco="visaoGeral">
          <dl className="flex flex-col gap-2">
            {(
              [
                ['sem_proxima_tarefa', semTarefa],
                ['sem_interacoes', semInteracao],
                ['recem_alocadas', recentes],
              ] as const
            ).map(([k, v]) => (
              <div key={k} className="flex justify-between rounded-lg bg-sup-2 px-4 py-3 text-sm text-tinta-2" data-numero={k}>
                <dt>{t(`crm.relatorio.resumo.${k}`)}</dt>
                <dd className="numero font-bold text-tinta-1">{numero(v)}</dd>
              </div>
            ))}
          </dl>
          <div className="flex flex-col gap-1" data-funil-vendas="">
            <h3 className="mb-2 font-semibold text-tinta-2">{t('crm.relatorio.tipos.funilVendas')}</h3>
            {abertas.length ? (
              abertasEtapas.map((e, i) => {
                const n = abertas.filter((x) => x.etapa_id === e.id).length;
                return (
                  // Rodada rlote-2 (VIS-M4): como as `Barras`, a cor fica só na faixa (o formato de funil, centrada) e o nome e o número
                  // vão em tinta de token por cima, legíveis nos dois temas (texto branco fixo sobre as cores dava menos de 3:1 no escuro).
                  <div key={e.id} className="flex flex-col items-center gap-1 text-xs" data-funil-etapa={e.nome}>
                    <span className="flex w-full items-baseline justify-between gap-3">
                      <span className="min-w-0 truncate font-medium text-tinta-2" title={e.nome}>
                        {e.nome}
                      </span>
                      <span className="numero shrink-0 text-tinta-1">{numero(n)}</span>
                    </span>
                    <span className="block h-3 rounded" style={{ width: `${30 + (70 * n) / maxFunil}%`, background: cor(i) }} aria-hidden />
                  </div>
                );
              })
            ) : (
              <p className="grid h-28 place-items-center text-sm text-tinta-3">{t('crm.relatorio.tipos.semDados')}</p>
            )}
          </div>
        </div>
      </Cartao>
    </>
  );
}

function Pipeline({ d, moeda }: { d: DadosRelatorioTipos; moeda: string }) {
  const { t } = useTranslation('pipeline');
  const { abertas } = recortes(d);
  if (!abertas.length)
    return (
      <Cartao>
        <Vazio texto={t('crm.relatorio.tipos.pipelineVazio')} />
      </Cartao>
    );
  return (
    <>
      {d.etapas
        .filter((e) => e.tipo === 'aberta')
        .map((e) => {
          const ns = abertas.filter((n) => n.etapa_id === e.id);
          return (
            <Cartao
              key={e.id}
              titulo={t('crm.relatorio.tipos.etapaResumo', {
                etapa: e.nome,
                n: numero(ns.length),
                valor: somaEm(ns, moeda),
              })}
            >
              <TabelaNegociacoes ns={ns} etapas={d.etapas} />
            </Cartao>
          );
        })}
    </>
  );
}

function Equipes({ d, moeda }: { d: DadosRelatorioTipos; moeda: string }) {
  const { t } = useTranslation('pipeline');
  const [aba, setAba] = useState<'equipes' | 'pipeline'>('equipes');
  const { ganhas, abertas } = recortes(d);
  const equipe = (n: NegociacaoRel) => n.equipe_nome ?? t('crm.relatorio.tipos.semEquipe');
  return (
    <>
      <Abas abas={['equipes', 'pipeline'] as const} atual={aba} aoMudar={setAba} rotulo={(a) => t(`crm.relatorio.tipos.abas.${a}`)} />
      {aba === 'equipes' ? (
        <>
          <Cartao titulo={t('crm.relatorio.tipos.vendasValores')}>
            <PorMoeda moedas={moedasDe(ganhas, moeda)}>
              {(m) => <Barras dado="equipesValor" linhas={agrupar(ganhas.filter((n) => n.moeda === m), equipe, (n) => n.total_centavos)} corBarra="var(--color-verde)" formatar={(v) => dinheiro(v, m)} />}
            </PorMoeda>
          </Cartao>
          <Cartao titulo={t('crm.relatorio.tipos.vendasUnidades')}>
            <Barras dado="equipesUnidades" linhas={agrupar(ganhas, equipe)} />
          </Cartao>
        </>
      ) : (
        <Cartao titulo={t('crm.relatorio.tipos.pipelineEquipe')}>
          <Barras dado="equipesPipeline" linhas={agrupar(abertas, equipe)} />
        </Cartao>
      )}
    </>
  );
}

function Feedback({ d }: { d: DadosRelatorioTipos }) {
  const { t } = useTranslation('pipeline');
  const [etapa, setEtapa] = useState('');
  const ns = recortes(d).abertas.filter((n) => !etapa || n.etapa_id === etapa);
  const faixas = (['ate7', 'ate30', 'mais30', 'sem'] as const).map((f) => [t(`crm.relatorio.tipos.faixas.${f}`), ns.filter((n) => faixaDoContato(n.ultimo_contato) === f).length] as [string, number]);
  return (
    <>
      <Cartao
        titulo={t('crm.relatorio.tipos.periodoContato')}
        acao={
          <div className="w-56">
            <Selecao
              aria-label={t('crm.relatorio.tipos.etapa')}
              value={etapa}
              vazio={t('crm.relatorio.tipos.selecioneEtapa')}
              opcoes={d.etapas.map((e) => ({ valor: e.id, rotulo: e.nome }))}
              onChange={(e) => setEtapa(e.target.value)}
              data-feedback-etapa=""
            />
          </div>
        }
      >
        {ns.length ? <Barras dado="faixas" linhas={faixas} /> : <Vazio />}
      </Cartao>
      <Cartao titulo={t('crm.relatorio.tipos.porUsuario')}>
        <Barras
          dado="porUsuario"
          corBarra="var(--color-roxo)"
          linhas={agrupar(
            ns.filter((n) => n.responsavel_nome),
            (n) => nomePessoa(n.responsavel_nome!),
          )}
        />
      </Cartao>
      <Cartao titulo={t('crm.relatorio.tipos.filtradas', { n: numero(ns.length) })}>
        <TabelaNegociacoes ns={ns} etapas={d.etapas} />
      </Cartao>
    </>
  );
}

function Conversoes({ d }: { d: DadosRelatorioTipos }) {
  const { t } = useTranslation('pipeline');
  const { todas, ganhas, perdidas } = recortes(d);
  const chegaram = chegaramEm(d.etapas, todas);
  const lista = (ns: NegociacaoRel[], final: number) => (
    <dl className="flex flex-col">
      {d.etapas.map((e) => (
        <div key={e.id} className="flex justify-between border-b border-borda py-1.5 text-sm text-tinta-2">
          <dt>{e.nome}</dt>
          <dd className="numero font-semibold">{numero(ns.filter((n) => n.etapa_id === e.id).length)}</dd>
        </div>
      ))}
      <div className="flex justify-between py-1.5 text-sm text-tinta-2">
        <dt>{t('crm.relatorio.tipos.conversaoFinal')}</dt>
        <dd className="numero font-semibold">{numero(final)}</dd>
      </div>
    </dl>
  );
  return (
    <>
      <Cartao titulo={t('crm.relatorio.tipos.conversaoVolume')}>
        <div className="flex flex-col gap-3">
          <Legenda
            itens={[
              [t('crm.relatorio.tipos.negociacoes'), 'var(--color-ciano)'],
              [t('crm.relatorio.tipos.vendas'), 'var(--color-verde)'],
              [t('crm.relatorio.tipos.perdidas'), 'var(--color-perigo)'],
            ]}
          />
          <Barras dado="conversao" linhas={[...d.etapas.map((e, i) => [e.nome, chegaram[i]!] as [string, number]), [t('crm.relatorio.tipos.conversaoFinal'), ganhas.length]]} />
        </div>
      </Cartao>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Cartao titulo={<span className="text-perigo">{t('crm.relatorio.tipos.perdidosEtapa')}</span>}>{lista(perdidas, perdidas.length)}</Cartao>
        <Cartao titulo={<span className="text-verde">{t('crm.relatorio.tipos.vendasEtapa')}</span>}>{lista(ganhas, ganhas.length)}</Cartao>
      </div>
    </>
  );
}

function Produtos({ d, moeda }: { d: DadosRelatorioTipos; moeda: string }) {
  const { t } = useTranslation('pipeline');
  const [aba, setAba] = useState<'produtos' | 'pipeline'>('produtos');
  const { ganhas, abertas } = recortes(d);
  const itens = (ns: NegociacaoRel[]) => ns.flatMap((n) => n.produtos);
  const naMoeda = (ns: NegociacaoRel[], m: string) => itens(ns.filter((n) => n.moeda === m));
  return (
    <>
      <Abas abas={['produtos', 'pipeline'] as const} atual={aba} aoMudar={setAba} rotulo={(a) => t(`crm.relatorio.tipos.abas.${a}`)} />
      {aba === 'pipeline' ? (
        <Cartao titulo={t('crm.relatorio.tipos.produtosPipeline')}>
          <PorMoeda moedas={moedasDe(abertas, moeda)}>
            {(m) => (
              <Barras
                dado="produtosPipeline"
                linhas={agrupar(
                  naMoeda(abertas, m),
                  (p) => p.nome,
                  (p) => p.total_centavos,
                )}
                formatar={(v) => dinheiro(v, m)}
              />
            )}
          </PorMoeda>
        </Cartao>
      ) : (
        <>
          <Cartao titulo={t('crm.relatorio.tipos.produtosValor')}>
            <PorMoeda moedas={moedasDe(ganhas, moeda)}>
              {(m) => (
                <Barras
                  dado="produtosValor"
                  corBarra="var(--color-verde)"
                  linhas={agrupar(
                    naMoeda(ganhas, m),
                    (p) => p.nome,
                    (p) => p.total_centavos,
                  )}
                  formatar={(v) => dinheiro(v, m)}
                />
              )}
            </PorMoeda>
          </Cartao>
          <Cartao titulo={t('crm.relatorio.tipos.produtosQuantidade')}>
            <Barras
              dado="produtosQuantidade"
              linhas={agrupar(
                itens(ganhas),
                (p) => p.nome,
                (p) => p.quantidade,
              )}
            />
          </Cartao>
          <p className="text-center text-sm text-tinta-3">{t('crm.relatorio.tipos.produtosNota')}</p>
        </>
      )}
    </>
  );
}

function Fontes({ d }: { d: DadosRelatorioTipos }) {
  const { t } = useTranslation('pipeline');
  const [aba, setAba] = useState<'fontes' | 'campanhas'>('fontes');
  const [ordem, setOrdem] = useState<'criadas' | 'vendidas' | 'perdidas'>('criadas');
  const { todas, ganhas, perdidas, abertas } = recortes(d);
  const deFonte = aba === 'fontes';
  const chave = (n: NegociacaoRel, fonte: boolean) => (fonte ? (n.fonte ?? t('crm.relatorio.tipos.semFonte')) : (n.campanha ?? t('crm.relatorio.tipos.semCampanha')));
  const base = ordem === 'vendidas' ? ganhas : ordem === 'perdidas' ? perdidas : todas;
  return (
    <>
      <Abas abas={['fontes', 'campanhas'] as const} atual={aba} aoMudar={setAba} rotulo={(a) => t(`crm.relatorio.tipos.abas.${a}`)} />
      <Cartao
        titulo={t(deFonte ? 'crm.relatorio.tipos.fontesOrdenadas' : 'crm.relatorio.tipos.campanhasOrdenadas')}
        acao={
          <div className="w-40">
            <Selecao
              aria-label={t('crm.relatorio.tipos.ordenarPor')}
              value={ordem}
              opcoes={(['criadas', 'vendidas', 'perdidas'] as const).map((o) => ({
                valor: o,
                rotulo: t(`crm.relatorio.tipos.ordens.${o}`),
              }))}
              onChange={(e) => setOrdem(e.target.value as typeof ordem)}
              data-fontes-ordem=""
            />
          </div>
        }
      >
        <Barras dado="fontes" linhas={agrupar(base, (n) => chave(n, deFonte)).sort((a, b) => b[1] - a[1])} />
      </Cartao>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Cartao titulo={t(deFonte ? 'crm.relatorio.tipos.campanhas' : 'crm.relatorio.tipos.fontes')}>
          <Barras dado="outra" corBarra="var(--color-roxo)" linhas={agrupar(todas, (n) => chave(n, !deFonte))} />
        </Cartao>
        <Cartao titulo={t('crm.relatorio.tipos.responsaveis')}>
          <Barras
            dado="responsaveis"
            linhas={agrupar(
              todas.filter((n) => n.responsavel_nome),
              (n) => nomePessoa(n.responsavel_nome!),
            )}
          />
        </Cartao>
        <Cartao titulo={t('crm.relatorio.tipos.statusFinal')}>
          <Barras
            dado="status"
            corBarra="var(--color-verde)"
            linhas={[
              [t('crm.relatorio.tipos.andamento'), abertas.length],
              [t('crm.relatorio.tipos.vendidas'), ganhas.length],
              [t('crm.relatorio.tipos.perdidas'), perdidas.length],
            ]}
          />
        </Cartao>
      </div>
      <Cartao titulo={t('crm.relatorio.tipos.filtradas', { n: numero(todas.length) })}>
        <TabelaNegociacoes ns={todas} etapas={d.etapas} />
      </Cartao>
    </>
  );
}

function Ligacoes({ d }: { d: DadosRelatorioTipos }) {
  const { t } = useTranslation('pipeline');
  const dias = ultimos30Dias();
  // Rodada rlote-6 (ROB-B2): "Realizadas no período" são as dos 30 dias do gráfico ao lado, não todas as que vieram.
  const porDiaLigacoes = porDia(d.ligacoes, (l) => l.concluida_em, dias);
  return (
    <Cartao titulo={t('crm.relatorio.tipos.ligacoesPeriodo')}>
      <div className="flex flex-col gap-3" data-bloco="ligacoes">
        <Numeros
          itens={[
            {
              rotulo: t('crm.relatorio.tipos.realizadas'),
              valor: numero(porDiaLigacoes.reduce((a, b) => a + b, 0)),
              cor: 'text-ciano',
              dado: 'realizadas',
            },
          ]}
        />
        <Legenda itens={[[t('crm.relatorio.tipos.realizadas'), 'var(--color-ciano)']]} />
        <GraficoLinha
          dias={dias}
          rotulo={t('crm.relatorio.tipos.ligacoesPeriodo')}
          series={[
            {
              valores: porDiaLigacoes,
              cor: 'var(--color-ciano)',
            },
          ]}
        />
        <p className="text-xs text-tinta-3">{t('crm.relatorio.tipos.ligacoesNota')}</p>
      </div>
    </Cartao>
  );
}

function Comparativo({ d }: { d: DadosRelatorioTipos }) {
  const { t } = useTranslation('pipeline');
  const [aba, setAba] = useState<'equipes' | 'individual'>('equipes');
  const grupos = agrupar(d.ligacoes, (l) =>
    aba === 'equipes' ? (l.equipe_nome ?? t('crm.relatorio.tipos.semEquipe')) : l.responsavel_nome ? nomePessoa(l.responsavel_nome) : t('crm.relatorio.tipos.semResponsavel'),
  );
  return (
    <>
      <Abas abas={['equipes', 'individual'] as const} atual={aba} aoMudar={setAba} rotulo={(a) => t(`crm.relatorio.tipos.abas.${a}`)} />
      <Cartao titulo={t('crm.relatorio.tipos.ligacoesRealizadas')}>
        <div className="flex flex-col gap-3">
          <Numeros
            itens={[
              {
                rotulo: t('crm.relatorio.tipos.totalGeral'),
                valor: numero(d.ligacoes.length),
                dado: 'totalGeral',
              },
            ]}
          />
          <Barras dado="comparativo" linhas={grupos} />
        </div>
      </Cartao>
    </>
  );
}

interface Meta {
  id: string;
  alvo: 'equipe' | 'vendedor';
  alvo_nome: string;
  valor_centavos: number;
  moeda: string;
  realizado_centavos: number;
}

function Metas({ moeda }: { moeda: string }) {
  const { t } = useTranslation('pipeline');
  const [aba, setAba] = useState<'individuais' | 'equipe' | 'empresa'>('individuais');
  const q = useQuery({
    queryKey: ['pipeline', 'metas', 'relatorio'],
    queryFn: () => get<{ itens: Meta[]; mes: string }>('/pipeline/metas'),
  });
  if (q.isError && !q.data) return <Erro erro={q.error} aoTentar={() => void q.refetch()} />;
  if (!q.data) return <Carregando texto={t('crm.relatorio.carregando')} />;
  const metas = q.data.itens.filter((m) => (aba === 'individuais' ? m.alvo === 'vendedor' : aba === 'equipe' ? m.alvo === 'equipe' : false));
  let corpo: ReactNode;
  if (!metas.length)
    corpo = (
      <Cartao>
        <div className="flex flex-col items-center gap-2 px-4 py-10 text-center" data-relatorio-metas-vazio="">
          <p className="text-xl font-semibold">{t('crm.relatorio.tipos.crieMeta')}</p>
          <p className="text-sm text-tinta-3">{t('crm.relatorio.tipos.crieMetaTexto')}</p>
          <Link to="/comercial/pipeline/metas" className="mt-2 rounded-lg bg-acento-forte px-4 py-2 text-sm font-semibold text-sup-1 hover:bg-acento-hover" data-criar-meta="">
            {t('crm.relatorio.tipos.criarMeta')}
          </Link>
        </div>
      </Cartao>
    );
  else
    corpo = (
      <Cartao titulo={t('crm.relatorio.tipos.metasMes')}>
        <PorMoeda moedas={moedasDe(metas, moeda)}>
          {(mo) => (
            <Barras
              dado="metas"
              corBarra="var(--color-verde)"
              linhas={metas.filter((m) => m.moeda === mo).map((m) => [`${aba === 'individuais' ? nomePessoa(m.alvo_nome) : m.alvo_nome} (${dinheiro(m.valor_centavos, m.moeda)})`, m.realizado_centavos] as [string, number])}
              formatar={(v) => dinheiro(v, mo)}
            />
          )}
        </PorMoeda>
      </Cartao>
    );
  return (
    <div className="flex min-w-0 flex-col gap-4" data-relatorio-tipo="metas">
      <Abas abas={['individuais', 'equipe', 'empresa'] as const} atual={aba} aoMudar={setAba} rotulo={(a) => t(`crm.relatorio.tipos.abas.${a}`)} />
      {corpo}
    </div>
  );
}
